/**
 * Demo dataset: a six-unit guesthouse in Tyrol, roughly the size of the pilot
 * landlord. Used when no Supabase project is configured, so the app is
 * demonstrable on a phone without a backend.
 */

import { addDays, todayISO } from '../lib/dates'
import { priceStay } from '../lib/pricing'
import type { Database } from './model'

const id = (p: string, n: number) => `${p}-${String(n).padStart(4, '0')}`

export function buildDemoDatabase(): Database {
  const today = todayISO()
  const year = today.slice(0, 4)
  const T = 'demo'

  const properties = [{
    id: 'prop-1', tenant_id: T, name: 'Haus Verena',
    address: 'Dorfstraße 12', postcode: '6450', city: 'Sölden', country: 'AT',
    checkin_from: '15:00', checkout_by: '10:00', sort_order: 0, active: true,
  }]

  // Two categories, so the demo shows category pricing rather than only per-unit.
  const unit_categories: Database['unit_categories'] = [
    {
      id: 'cat-fw', tenant_id: T, name: 'Ferienwohnung', kind: 'apartment',
      default_occupancy: 2, default_max_occupancy: 4, default_price: 95,
      default_min_nights: 2, color: '#0ea5e9', sort_order: 0, active: true,
    },
    {
      id: 'cat-dz', tenant_id: T, name: 'Doppelzimmer', kind: 'room',
      default_occupancy: 2, default_max_occupancy: 2, default_price: 65,
      default_min_nights: 1, color: '#14b8a6', sort_order: 1, active: true,
    },
  ]

  const unitDefs: [string, string, 'apartment' | 'room', number, number, number, string, string][] = [
    ['Enzian',      'A1', 'apartment', 4, 2,  95, '#0ea5e9', 'cat-fw'],
    ['Edelweiß',    'A2', 'apartment', 4, 2, 105, '#8b5cf6', 'cat-fw'],
    ['Almrausch',   'A3', 'apartment', 6, 4, 135, '#f97316', 'cat-fw'],
    ['Zimmer Nord', 'Z1', 'room',      2, 2,  65, '#14b8a6', 'cat-dz'],
    ['Zimmer Süd',  'Z2', 'room',      2, 2,  65, '#f43f5e', 'cat-dz'],
    ['Studio Tal',  'S1', 'apartment', 2, 2,  80, '#84cc16', 'cat-fw'],
  ]

  const units: Database['units'] = unitDefs.map(
    ([name, code, kind, max, base, price, color, categoryId], i) => ({
    id: id('unit', i + 1), tenant_id: T, property_id: 'prop-1',
    name, code, kind, category_id: categoryId, max_occupancy: max, base_occupancy: base,
    bedrooms: kind === 'room' ? 1 : 2, size_sqm: kind === 'room' ? 22 : 54,
    base_price: price, extra_person_price: 18, min_nights: kind === 'room' ? 1 : 2,
    color, description: {}, amenities: ['wifi', 'parking'],
    sort_order: i, active: true,
  }))

  const rate_periods = [
    {
      id: 'rate-1', tenant_id: T, unit_id: null, name: 'Nebensaison',
      valid_from: `${year}-01-01`, valid_to: `${year}-12-31`, dow_mask: 127,
      price_per_night: 89, extra_person_price: 18, base_occupancy: null,
      min_nights: 2, priority: 0, active: true,
    },
    {
      id: 'rate-2', tenant_id: T, unit_id: null, name: 'Hochsaison Sommer',
      valid_from: `${year}-07-01`, valid_to: `${year}-09-15`, dow_mask: 127,
      price_per_night: 129, extra_person_price: 22, base_occupancy: null,
      min_nights: 3, priority: 5, active: true,
    },
    {
      id: 'rate-3', tenant_id: T, unit_id: null, name: 'Wochenendzuschlag',
      valid_from: `${year}-01-01`, valid_to: `${year}-12-31`, dow_mask: 0b1100000,
      price_per_night: 149, extra_person_price: 22, base_occupancy: null,
      min_nights: null, priority: 8, active: true,
    },
  ]

  const fees = [
    {
      id: 'fee-1', tenant_id: T, unit_id: null, code: 'cleaning',
      name: { de: 'Endreinigung', en: 'Final cleaning' },
      basis: 'per_stay' as const, amount: 65, vat_rate: 20, mandatory: true,
      taxable_age_from: null, max_nights: null, sort_order: 1, active: true,
    },
    {
      id: 'fee-2', tenant_id: T, unit_id: null, code: 'linen',
      name: { de: 'Bettwäsche & Handtücher', en: 'Linen & towels' },
      basis: 'per_person' as const, amount: 12, vat_rate: 20, mandatory: false,
      taxable_age_from: null, max_nights: null, sort_order: 2, active: true,
    },
    {
      id: 'fee-3', tenant_id: T, unit_id: null, code: 'pet',
      name: { de: 'Haustier', en: 'Pet' },
      basis: 'per_night' as const, amount: 8, vat_rate: 20, mandatory: false,
      taxable_age_from: null, max_nights: null, sort_order: 3, active: true,
    },
  ]

  const guestNames: [string, string, string][] = [
    ['Martina', 'Huber', 'martina.huber@example.at'],
    ['Peter', 'Schmidt', 'p.schmidt@example.de'],
    ['Sarah', 'O’Connor', 'sarah.oconnor@example.ie'],
    ['Luca', 'Bianchi', 'l.bianchi@example.it'],
    ['Anna', 'Novák', 'anna.novak@example.cz'],
    ['James', 'Whitfield', 'j.whitfield@example.co.uk'],
    ['Familie', 'Berger', 'berger@example.at'],
    ['Kevin', 'Mayr', 'kevin.mayr@example.at'],
  ]

  /*
   * Geburtstage und Werbeeinwilligung.
   *
   * Relativ zu heute, damit die Karte „Geburtstag demnächst" in der Demo auch
   * wirklich etwas zeigt — ein festes Datum wäre elf Monate im Jahr unsichtbar.
   * Das Jahr wird zurückdatiert, sonst wäre der Gast null Jahre alt.
   *
   * Die Einwilligung hat ABSICHTLICH nicht jeder: gerade der Fall „Geburtstag in
   * drei Tagen, aber keine Einwilligung" ist der, den man in der Demo sehen soll.
   */
  const birthdayIn = (days: number, ageYears: number): string => {
    const d = addDays(today, days)
    return `${Number(d.slice(0, 4)) - ageYears}${d.slice(4)}`
  }
  const extras: { birthdate?: string; consent?: boolean }[] = [
    { birthdate: birthdayIn(4, 43), consent: true },     // Martina — gleich dran
    { birthdate: birthdayIn(11, 58), consent: false },   // Peter — dran, aber gesperrt
    { birthdate: birthdayIn(26, 35), consent: true },    // Sarah — im Monatsfilter
    { birthdate: birthdayIn(120, 29) },
    { birthdate: birthdayIn(200, 47), consent: true },
    { birthdate: birthdayIn(240, 61) },
    {},                                                   // Familie: kein Geburtsdatum
    { birthdate: birthdayIn(310, 24) },
  ]

  const guests = guestNames.map(([first, last, email], i) => ({
    id: id('guest', i + 1), tenant_id: T, first_name: first, last_name: last, email,
    phone: '+43 660 1234' + (100 + i),
    country: email.split('.').pop()!.toUpperCase().slice(0, 2),
    language: email.endsWith('.at') || email.endsWith('.de') ? 'de' : 'en',
    blacklisted: false,
    ...(extras[i]?.birthdate ? { birthdate: extras[i]!.birthdate } : {}),
    ...(extras[i]?.consent
      ? {
        marketing_consent: true,
        marketing_consent_at: `${addDays(today, -200)}T10:00:00.000Z`,
        marketing_consent_source: 'Anfrageformular, Feld „Ich möchte Neuigkeiten erhalten"',
      }
      : {}),
  }))

  // Spread bookings around today so the calendar always looks alive.
  const plan: [number, number, number, string, number, number][] = [
    // [unitIndex, startOffset, nights, status, adults, children]
    [0, -3, 7, 'checked_in', 2, 1],
    [1, 0, 5, 'confirmed', 2, 0],   // arrives today
    [3, -4, 4, 'checked_in', 2, 0], // departs today
    [1, 6, 5, 'confirmed', 2, 0],
    [2, -1, 4, 'checked_in', 4, 2],
    [3, 2, 2, 'confirmed', 1, 0],
    [4, 5, 3, 'option', 2, 0],
    [0, 8, 6, 'confirmed', 2, 2],
    [5, -6, 4, 'checked_out', 2, 0],
    [1, 13, 4, 'enquiry', 2, 0],
    [2, 12, 7, 'confirmed', 5, 1],
    [3, 6, 3, 'quoted', 2, 0],
    [4, 14, 5, 'confirmed', 2, 1],
    [5, 3, 4, 'confirmed', 2, 0],
  ]

  const bookings: Database['bookings'] = plan.map(([u, offset, n, status, adults, children], i) => {
    const arrival = addDays(today, offset)
    return {
      id: id('bk', i + 1), tenant_id: T,
      unit_id: units[u]!.id,
      guest_id: guests[i % guests.length]!.id,
      reference: `B-${year}-${String(i + 1).padStart(4, '0')}`,
      kind: 'booking' as const,
      status: status as Database['bookings'][number]['status'],
      // A hold the landlord has to chase: lapses in two days, so the demo shows the
      // reminder on Heute rather than only the calendar bar.
      ...(status === 'option' ? { option_until: addDays(today, 2) } : {}),
      channel: (['direct', 'booking_com', 'airbnb', 'website'] as const)[i % 4]!,
      arrival, departure: addDays(arrival, n),
      adults, children,
      children_ages: children > 0 ? Array.from({ length: children }, (_, k) => 5 + k * 4) : [],
      optional_fee_ids: i % 5 === 0 ? ['fee-2'] : [],
      currency: 'EUR',
      total_net: 0, total_vat: 0, total_gross: 0,
      prepayment_amount: 0, prepayment_due: null,
      deposit_amount: u < 3 ? 200 : 0,
      deposit_status: (u < 3 ? 'due' : 'not_required') as Database['bookings'][number]['deposit_status'],
      internal_notes: '',
      created_at: new Date().toISOString(),
    }
  })

  // One maintenance block so the calendar shows the difference visually.
  bookings.push({
    id: 'bk-block-1', tenant_id: T, unit_id: units[5]!.id, guest_id: null,
    reference: `S-${year}-0001`, kind: 'block', status: 'confirmed', channel: 'direct',
    arrival: addDays(today, 20), departure: addDays(today, 24),
    adults: 0, children: 0, children_ages: [], optional_fee_ids: [], currency: 'EUR',
    total_net: 0, total_vat: 0, total_gross: 0, prepayment_amount: 0, prepayment_due: null,
    deposit_amount: 0, deposit_status: 'not_required',
    internal_notes: 'Badsanierung', created_at: new Date().toISOString(),
  })

  // An imported portal event that clashes with an existing booking, so the
  // conflict handling is visible in the demo rather than theoretical.
  const ical_feeds = [{
    id: 'feed-1', tenant_id: T, unit_id: units[1]!.id, direction: 'import' as const,
    label: 'Booking.com', url: 'https://ical.booking.com/v1/export?t=demo',
    token: 'demo-token-1', active: true,
    last_sync_at: new Date().toISOString(), last_status: 'ok', event_count: 1,
  }]

  const ical_events = [{
    id: 'ie-1', tenant_id: T, feed_id: 'feed-1', unit_id: units[1]!.id,
    uid: 'demo-uid-1', arrival: addDays(today, 2), departure: addDays(today, 4),
    summary: 'CLOSED - Booking.com', booking_id: null, dismissed: false,
  }]

  // Hoisted out of the returned object: the pricing pass below needs the VAT
  // settings, and there must be exactly one copy of them.
  // Woran der Store erkennt, dass ein gespeicherter Abzug von gestern ist.
  const seededOn = today

  const tenant: Database['tenant'] = {
    id: T, slug: 'demo', name: 'Haus Verena',
    country: 'AT', currency: 'EUR', locale: 'de', timezone: 'Europe/Vienna',
    vat_scheme: 'standard', vat_rate_accommodation: 10, vat_rate_services: 20,
    prices_include_vat: true,
    legal_name: 'Haus Verena GmbH',
    street: 'Dorfstraße 12', postcode: '6450', city: 'Sölden',
    address: 'Dorfstraße 12, 6450 Sölden',
    vat_id: 'ATU12345678', email: 'info@haus-verena.example',
    bank_holder: 'Haus Verena GmbH', bank_iban: 'AT61 1904 3002 3457 3201',
    bank_bic: 'BKAUATWW', accent_color: '#0f766e',
  }

  // Price every demo booking with the real engine rather than storing zeroes.
  // A prospect opening the demo saw "€ 0,00" against every arrival, which reads as a
  // broken product; and pricing them here also exercises the same code the live app
  // runs on save, so the demo cannot quietly disagree with reality.
  for (const b of bookings) {
    if (b.kind === 'block') continue
    const unit = units.find((u) => u.id === b.unit_id)
    if (!unit) continue
    const priced = priceStay({
      unit,
      ratePeriods: rate_periods,
      fees,
      touristTax: DEMO_TOURIST_TAX,
      arrival: b.arrival,
      departure: b.departure,
      adults: b.adults,
      children: b.children,
      childrenAges: b.children_ages,
      optionalFeeIds: b.optional_fee_ids,
      vatScheme: tenant.vat_scheme,
      vatRateAccommodation: tenant.vat_rate_accommodation,
      pricesIncludeVat: tenant.prices_include_vat ?? false,
    })
    b.total_net = priced.totals.net
    b.total_vat = priced.totals.vat
    b.total_gross = priced.totals.gross
    b.prepayment_amount = Math.round(priced.totals.gross * 0.3)
  }

  return {
    seeded_on: seededOn,
    tenant,
    properties,
    unit_categories,
    units,
    rate_periods,
    fees,
    guests,
    bookings,
    booking_items: [],
    quotes: [],
    invoices: [],
    invoice_items: [],
    payments: [],
    ical_feeds,
    ical_events,
  }
}

export const DEMO_TOURIST_TAX = {
  id: 'tt-1',
  municipality: 'Sölden',
  name: { de: 'Ortstaxe', en: 'Tourist tax' },
  amount_per_person_night: 3.2,
  vat_rate: 0,
  exempt_under_age: 15,
  reduced_under_age: null,
  reduced_amount: null,
  max_nights: null,
}
